/**
 * useConversationScroll Hook
 * Manages scroll behavior and refs for conversation list
 */

import React from 'react';
import { ChatMessage } from '@/types/chatTypes';

export function useConversationScroll(
  messages: ChatMessage[],
  isLoading: boolean | undefined,
  scrollToTopRef:
    | React.RefObject<{
        scrollToTop: () => void;
        scrollContainerToTop: () => void;
      }>
    | undefined,
  isLoadingFollowUps?: boolean
) {
  const scrollableContainerRef = React.useRef<HTMLDivElement>(null);
  const loadingPlaceholderRef = React.useRef<HTMLDivElement>(null);
  const lastResponsePairRef = React.useRef<HTMLDivElement>(null);
  const lastTabsSectionRef = React.useRef<HTMLDivElement>(null);
  const lastHeaderRef = React.useRef<HTMLDivElement>(null);
  const lastDividerAfterTabsRef = React.useRef<HTMLDivElement>(null);

  // Check if we're waiting for a response
  // Show loading placeholder whenever API is loading, regardless of messages
  const isWaitingForResponse = React.useMemo(() => {
    return isLoading === true;
  }, [isLoading]);

  // Get the last user message
  const lastUserMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return messages[i];
      }
    }
    return null;
  }, [messages]);

  const scrollToTopTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Scroll to show current/latest chat
  const scrollToTop = React.useCallback(() => {
    // Clear any pending scroll
    if (scrollToTopTimeoutRef.current) {
      clearTimeout(scrollToTopTimeoutRef.current);
    }

    // Prevent multiple rapid scrolls within 200ms
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 200) {
      return; // Skip if too recent
    }
    lastScrollTimeRef.current = now;

    // Prevent multiple simultaneous scrolls
    if (isScrollingRef.current) {
      return;
    }

    isScrollingRef.current = true;

    scrollToTopTimeoutRef.current = setTimeout(() => {
      if (
        isWaitingForResponse &&
        loadingPlaceholderRef.current &&
        scrollableContainerRef.current
      ) {
        loadingPlaceholderRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      } else if (
        lastResponsePairRef.current &&
        scrollableContainerRef.current
      ) {
        lastResponsePairRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      } else if (scrollableContainerRef.current) {
        scrollableContainerRef.current.scrollTo({
          top: scrollableContainerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }

      // Reset scrolling flag after scroll completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 300);
    }, 200);
  }, [isWaitingForResponse]);

  // Scroll current chat to top of viewport (for tab switching)
  // This shows the current chat at the top with previous chats scrollable above
  // Scroll to intent and mood section (header) for all tabs - no other elements should be visible above
  const scrollContainerToTop = React.useCallback(() => {
    // Prevent multiple simultaneous scrolls
    if (isScrollingRef.current) {
      return;
    }

    isScrollingRef.current = true;

    // Helper function to scroll to a specific element position
    const scrollToElement = (
      element: HTMLElement | null,
      container: HTMLElement | null
    ) => {
      if (!element || !container) return false;

      // Use a more reliable method: calculate offsetTop by traversing up to container
      let offsetTop = 0;
      let currentElement: HTMLElement | null = element;
      let parent: HTMLElement | null = null;

      // Traverse up the DOM tree until we reach the container
      while (currentElement && currentElement !== container) {
        offsetTop += currentElement.offsetTop;
        parent = currentElement.offsetParent as HTMLElement | null;

        // If offsetParent is null or not an HTMLElement, try parentElement
        if (!parent || parent === document.body) {
          parent = currentElement.parentElement;
        }

        currentElement = parent;

        // Safety check to prevent infinite loops
        if (
          !currentElement ||
          currentElement === document.body ||
          currentElement === document.documentElement
        ) {
          break;
        }
      }

      // Account for container's padding-top
      const containerStyle = window.getComputedStyle(container);
      const paddingTop = parseInt(containerStyle.paddingTop, 10) || 0;

      // Additional adjustment for mobile header margin
      // The header has mt-4 on mobile which adds 16px (1rem) margin
      const isMobile = window.innerWidth < 768; // md breakpoint
      const mobileMarginAdjustment = isMobile ? 16 : 0; // 1rem in pixels

      // Calculate final scroll position with mobile adjustment
      const scrollPosition = Math.max(
        0,
        offsetTop - paddingTop - mobileMarginAdjustment
      );

      // Use instant scroll to immediately position - this prevents previous content from showing
      container.scrollTop = scrollPosition;

      // Add a small delay before verification to prevent race conditions
      setTimeout(() => {
        // Verify the position is correct after a frame
        requestAnimationFrame(() => {
          // Check if scroll position is correct, adjust if needed
          const currentScroll = container.scrollTop;
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top;

          // Account for mobile margin in verification
          const isMobile = window.innerWidth < 768;
          const mobileMarginAdjustment = isMobile ? 16 : 0;
          const targetPosition = paddingTop + mobileMarginAdjustment;

          // Only adjust if significantly off position (increased tolerance)
          if (Math.abs(relativeTop - targetPosition) > 10) {
            // Increased from 5px to 10px
            const adjustedPosition =
              currentScroll + (relativeTop - targetPosition);
            container.scrollTop = adjustedPosition;
          }

          // Reset scrolling flag after scroll completes
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 200);
        });
      }, 100); // Add 100ms delay to prevent race conditions

      return true;
    };

    const performScroll = () => {
      let scrolled = false;

      // If waiting for response, scroll loading placeholder to top
      if (
        isWaitingForResponse &&
        loadingPlaceholderRef.current &&
        scrollableContainerRef.current
      ) {
        scrolled = scrollToElement(
          loadingPlaceholderRef.current,
          scrollableContainerRef.current
        );
      } else if (lastHeaderRef.current && scrollableContainerRef.current) {
        // Prefer scrolling to header section (intent and mood) for all tabs
        // This ensures intent and mood tags are at the top with no other elements visible above
        scrolled = scrollToElement(
          lastHeaderRef.current,
          scrollableContainerRef.current
        );
      } else if (
        lastDividerAfterTabsRef.current &&
        scrollableContainerRef.current
      ) {
        // Fallback to divider after tabs if header ref is not available
        scrolled = scrollToElement(
          lastDividerAfterTabsRef.current,
          scrollableContainerRef.current
        );
      } else if (lastTabsSectionRef.current && scrollableContainerRef.current) {
        // Fallback to tabs section if header ref is not available
        scrolled = scrollToElement(
          lastTabsSectionRef.current,
          scrollableContainerRef.current
        );
      } else if (
        lastResponsePairRef.current &&
        scrollableContainerRef.current
      ) {
        // Fallback to response pair if other refs are not available
        scrolled = scrollToElement(
          lastResponsePairRef.current,
          scrollableContainerRef.current
        );
      }

      // If no scroll was performed, reset the flag immediately
      if (!scrolled) {
        isScrollingRef.current = false;
      }
    };

    // Single scroll attempt with requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      performScroll();
    });
  }, [isWaitingForResponse]);

  // Expose scrollToTop and scrollContainerToTop via ref
  React.useImperativeHandle(
    scrollToTopRef,
    () => ({
      scrollToTop,
      scrollContainerToTop,
    }),
    [scrollToTop, scrollContainerToTop]
  );

  // Auto-scroll to loading placeholder when waiting for response
  React.useEffect(() => {
    if (
      isWaitingForResponse &&
      loadingPlaceholderRef.current &&
      scrollableContainerRef.current
    ) {
      const timeout = setTimeout(() => {
        if (loadingPlaceholderRef.current && scrollableContainerRef.current) {
          loadingPlaceholderRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isWaitingForResponse]);

  // iOS Safari fix: Prevent negative scroll positions and monitor scroll position
  React.useEffect(() => {
    const container = scrollableContainerRef.current;
    if (!container) return;

    // Function to fix scroll position
    const fixScrollPosition = () => {
      if (container.scrollTop < 0) {
        container.scrollTop = 0;
      }
    };

    // Fix immediately
    fixScrollPosition();

    // Monitor scroll events to prevent negative positions
    const handleScroll = () => {
      // Use requestAnimationFrame to check after scroll
      requestAnimationFrame(() => {
        if (container.scrollTop < 0) {
          container.scrollTop = 0;
        }
      });
    };

    // Monitor touch events on iOS
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touchY = e.touches[0].clientY;
      const scrollTop = container.scrollTop;

      // If scrolling up at the top, prevent overscroll
      if (scrollTop <= 0 && touchY > touchStartY) {
        // User is trying to scroll up past the top
        if (container.scrollTop < 0) {
          container.scrollTop = 0;
        }
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    // Also check periodically (iOS Safari can have timing issues)
    const intervalId = setInterval(fixScrollPosition, 100);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      clearInterval(intervalId);
    };
  }, []);

  // Track the last assistant message's follow-up questions to detect when they're added
  const lastAssistantMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return messages[i];
      }
    }
    return null;
  }, [messages]);

  const previousFollowUpQuestionsCountRef = React.useRef<number>(0);
  const lastMessageIdRef = React.useRef<string | undefined>(undefined);
  const previousIsLoadingFollowUpsRef = React.useRef<boolean | undefined>(
    undefined
  );
  // Prevent multiple simultaneous scrolls
  const isScrollingRef = React.useRef<boolean>(false);
  const lastScrollTimeRef = React.useRef<number>(0);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounced scroll function to prevent multiple simultaneous scrolls
  const debouncedScrollToHeader = React.useCallback(() => {
    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // If already scrolling, skip
    if (isScrollingRef.current) {
      return;
    }

    isScrollingRef.current = true;

    const scrollToElement = (
      element: HTMLElement | null,
      container: HTMLElement | null
    ) => {
      if (!element || !container) return false;

      let offsetTop = 0;
      let currentElement: HTMLElement | null = element;
      let parent: HTMLElement | null = null;

      while (currentElement && currentElement !== container) {
        offsetTop += currentElement.offsetTop;
        parent = currentElement.offsetParent as HTMLElement | null;

        if (!parent || parent === document.body) {
          parent = currentElement.parentElement;
        }

        currentElement = parent;

        if (
          !currentElement ||
          currentElement === document.body ||
          currentElement === document.documentElement
        ) {
          break;
        }
      }

      const containerStyle = window.getComputedStyle(container);
      const paddingTop = parseInt(containerStyle.paddingTop, 10) || 0;

      // Additional adjustment for mobile header margin
      const isMobile = window.innerWidth < 768;
      const mobileMarginAdjustment = isMobile ? 16 : 0;

      const scrollPosition = Math.max(
        0,
        offsetTop - paddingTop - mobileMarginAdjustment
      );
      container.scrollTop = scrollPosition;

      // Single verification after a frame
      requestAnimationFrame(() => {
        const currentScroll = container.scrollTop;
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;

        // Account for mobile margin in verification
        const isMobile = window.innerWidth < 768;
        const mobileMarginAdjustment = isMobile ? 16 : 0;
        const targetPosition = paddingTop + mobileMarginAdjustment;

        if (Math.abs(relativeTop - targetPosition) > 5) {
          const adjustedPosition =
            currentScroll + (relativeTop - targetPosition);
          container.scrollTop = adjustedPosition;
        }
      });

      return true;
    };

    const performScroll = () => {
      let scrolled = false;

      if (lastHeaderRef.current && scrollableContainerRef.current) {
        scrolled = scrollToElement(
          lastHeaderRef.current,
          scrollableContainerRef.current
        );
      } else if (
        lastDividerAfterTabsRef.current &&
        scrollableContainerRef.current
      ) {
        scrolled = scrollToElement(
          lastDividerAfterTabsRef.current,
          scrollableContainerRef.current
        );
      } else if (lastTabsSectionRef.current && scrollableContainerRef.current) {
        scrolled = scrollToElement(
          lastTabsSectionRef.current,
          scrollableContainerRef.current
        );
      }

      // Reset scrolling flag after a short delay (or immediately if no scroll was performed)
      if (scrolled) {
        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingRef.current = false;
        }, 300);
      } else {
        isScrollingRef.current = false;
      }
    };

    // Single scroll attempt with a small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      performScroll();
    });
  }, []);

  // When follow-up questions are added, maintain scroll position at header (chat title)
  // This prevents the page from auto-scrolling to the questions, which would hide the chat title and intent/mood
  React.useEffect(() => {
    const currentFollowUpQuestions = lastAssistantMessage?.followUpQuestions;
    const currentCount = currentFollowUpQuestions?.length || 0;
    const previousCount = previousFollowUpQuestionsCountRef.current;
    const currentMessageId = lastAssistantMessage?.id;
    const previousIsLoadingFollowUps = previousIsLoadingFollowUpsRef.current;

    // Reset tracking when a new assistant message is added (different message ID)
    if (currentMessageId && currentMessageId !== lastMessageIdRef.current) {
      previousFollowUpQuestionsCountRef.current = 0;
      lastMessageIdRef.current = currentMessageId;
      previousIsLoadingFollowUpsRef.current = undefined;
    }

    // Check if follow-up questions were just added (count increased from 0)
    // Only trigger when questions are first added, not when clicked
    const followUpQuestionsAdded =
      !isWaitingForResponse &&
      currentCount > 0 &&
      currentCount > previousCount &&
      previousCount === 0;

    // Check if follow-ups just finished loading (isLoadingFollowUps transitioned from true to false)
    const followUpsJustFinishedLoading =
      previousIsLoadingFollowUps === true &&
      isLoadingFollowUps === false &&
      currentCount > 0;

    // Trigger scroll only once when questions are first added or when loading finishes
    if (followUpQuestionsAdded || followUpsJustFinishedLoading) {
      debouncedScrollToHeader();
    }

    // Update the refs for next comparison
    previousFollowUpQuestionsCountRef.current = currentCount;
    previousIsLoadingFollowUpsRef.current = isLoadingFollowUps;
    if (currentMessageId) {
      lastMessageIdRef.current = currentMessageId;
    }

    // Cleanup on unmount
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollToTopTimeoutRef.current) {
        clearTimeout(scrollToTopTimeoutRef.current);
      }
    };
  }, [
    lastAssistantMessage?.id,
    lastAssistantMessage?.followUpQuestions,
    lastAssistantMessage?.followUpQuestions?.length,
    isWaitingForResponse,
    isLoadingFollowUps,
    debouncedScrollToHeader,
  ]);

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollToTopTimeoutRef.current) {
        clearTimeout(scrollToTopTimeoutRef.current);
      }
    };
  }, []);

  return {
    scrollableContainerRef,
    loadingPlaceholderRef,
    lastResponsePairRef,
    lastTabsSectionRef,
    lastHeaderRef,
    lastDividerAfterTabsRef,
    isWaitingForResponse,
    lastUserMessage,
  };
}
